import React, { useState, useLayoutEffect, useEffect, useCallback, useRef, memo } from "react"
import { Main } from "./styles"
import { motion, useTransform, transform, useScroll } from "framer-motion"
import { STEPS, VARIANTS, FADE_DURATION, FADE_EASE, WAVING_DELAY, STEPS_LENGTH } from "./../constants"
import { useWatchVariations, mvSubscribe } from "./../context"
import { useRoller } from "./../context.roller"
import { useMediaLoader } from "./../hooks"
import Image from "components/media/ImageCLD"
import { useEnv } from "contexts/Env"
import { StepListArrows } from "./arrows"
import { rafDelay } from "../utils"

export function buildPicture(v, catalogYear, step) {
  return {
    public_id: `catalogue/${catalogYear}/${CLD_FEATURES_MAP.get(step)}/${v.assets[step]}`,
    version: 1,
    width: 800,
    height: 1180,
    policy: "t_v7-main-configurator"
  }
}

const CLD_FEATURES_MAP = new Map([
  ["model", "upright-bba-with-shadow"],
  ["material", "configurator/material"],
  ["bezel", "configurator/bezel-with-shadow"],
  ["bracelet", "bracelet-highlight-on-full-watch"],
  ["dial", "configurator/raw-dial-with-shadow"],
])

function useItems(swapping, getModel) {
  const [items, setItems] = useState(null)
  const env = useEnv()
  const rfswapping = useRef(null)
  function clear() {
    rfswapping.current = null
    return setItems(null)
  }

  function update(v) {
    //console.log("useItems update swapping", v)
    if (!v) return
    if (rfswapping.current === v) return
    //    logmine("Callback", "WatchRoller", "useItems update", v)
    let [_stepindex, _start, ..._items] = v.split(",")
    if (+_stepindex <= 0) return clear()
    const step = STEPS[+_stepindex]
    _items = _items.map(getModel).map((v, i) => ({
      uid: v.uid,
      index: i,
      picture: buildPicture(v, env.catalogYear, step),
      caption: v.specs[+_stepindex + 2],
    }))
    //console.log("useItems update", _items)
    setItems(_items)
    rfswapping.current = v
    //    console.log("vvv items", _items)
    //    scrollto(+_start)

    //    console.log(_items)
    //    focus.set(_start)
  }

  useEffect(() => mvSubscribe("WatchRoller useItems swapping", swapping, update), [])

  return items
}

function Item({ ctx, index, picture, caption, stepindex, handleClick }) {
  const { x, W, N, R, focus } = useRoller(ctx)
  const onClick = e => handleClick(index)
  const rfmain = useRef(null)

  const ranger = useTransform([W, N, R], ([W, N, R]) => {
    let w = (W * R) / N
    let pos = w * index
    let inc = +(N >= 4)
    return transform(
      [
        pos - (inc + 1.25) * w,
        pos - (inc + 1) * w,
        pos - 0.75 * w,
        pos - 0.25 * w,
        pos,
        pos + 0.25 * w,
        pos + 0.75 * w,
        pos + (inc + 1) * w,
        pos + (inc + 1.25) * w,
      ],
      [1 - (N % 2), 1, 1, 0.25, 0, 0.25, 1, 1, 1 - (N % 2)]
    )
  })

  const opacity = useTransform([x, ranger, stepindex], ([x, ranger, stepindex]) => {
    return stepindex < 2 ? 1 : ranger(x)
  })

  function onFocus(v) {
    rfmain.current?.setAttribute("aria-current", v === index)
  }

  useEffect(() => mvSubscribe("", focus, onFocus), [])

  return (
    <motion.li ref={rfmain} onClick={onClick} style={{ opacity }}>
      <figure>
        <Image sources={[picture]} loading='eager' sizes='(min-width: 48rem) 33vw, 50vw' />
        <figcaption dangerouslySetInnerHTML={{ __html: caption }}></figcaption>
      </figure>
    </motion.li>
  )
}

export function StepList({ ctx, rctx, onFocusClick, attrs = null }) {
  const { rfroot, route, getModel, stepindex, newstepindex, swapping, slready, gready } =
    useWatchVariations(ctx)
  const { focus, rmfocus, x, W, N, R, count } = useRoller(rctx)
  const rfmain = useRef()
  const rflist = useRef()
  const rftm = useRef(null)
  const items = useItems(swapping, getModel)
  const ready = useMediaLoader("WVSL", rfmain, items)
  const rtl = process.browser && document.querySelector("html").getAttribute("dir") === "rtl"

  const { scrollX } = useScroll({
    container: rflist,
    axis: "x",
    offset: ["start end", "end end"],
  })

  const index = useTransform([scrollX, W, N, R], ([scrollX, W, N, R]) => {
    scrollX = Math.abs(scrollX)
    x.set(scrollX)
    return W === 0 ? -1 : ((N * scrollX) / (W * R) + 0.5) >> 0
  })

  function update(v) {
    clearTimeout(rftm.current)
    rftm.current = setTimeout(() => focus.set(v), 140)
  }

  function scrollto(v) {
    //    console.log("vvv scrollto", v)
    if (v === null) return
    const { current: list } = rflist
    if (!list) return
    let scroll = (W.get() * R.get() * v) / N.get()
    scroll = rtl ? -scroll : scroll
    list.scrollLeft = scroll
    rmfocus.set(null)
  }

  function onResize(e) {
    const { current: main } = rfmain
    if (!main) return
    W.set(main.offsetWidth)
    R.set(getComputedStyle(rflist.current).getPropertyValue("--wv-step-offset"))
    N.set(getComputedStyle(rflist.current).getPropertyValue("--wv-step-div"))
  }

  function onReady(v) {
    if (newstepindex.get() <= 0) {
      slready.set(true)
      rfmain.current?.classList.remove("wv_reveal")
      return
    }
    //    console.log("vvv onReady", v)
    slready.set(v)
    //    console.log("FooterDetail StepList onReady", slready.get(), gready.get())
    if (!v) rfmain.current?.classList.remove("wv_reveal")
  }

  function onGready(v) {
    //    console.log("StepListStepList onGready", incard, v)
    //console.log("onGready", v, stepindex.get())
    if (v && stepindex.get() > 0) rfmain.current?.classList.add("wv_reveal")
  }

  function onNewstepindex(v) {
    //    console.log("StepListStepList onNewstepindex", incard, v)
    onReady(v < 2)
  }

  function onRoute(v) {
    //    console.log("StepListStepList onRoute", incard, v)
    onReady(!v)
  }

  useLayoutEffect(() => {
    window.addEventListener("resize", onResize, false)
    window.addEventListener("orientationchange", onResize, false)
    //    setTimeout(onResize, 5000)
    return () => {
      window.removeEventListener("resize", onResize, false)
      window.removeEventListener("orientationchange", onResize, false)
    }
  }, [])

  useEffect(() => {
    //    console.log(">>>>> items", focus, rmfocus, x, W, N, count)
    if (!items) return
    onResize()
    scrollto(focus.get())
    //    rmfocus.set(-1)
    count.set(items.length)
  }, [items])

  useEffect(() => mvSubscribe("StepList index", index, update), [])
  useEffect(() => mvSubscribe("StepList rmfocus", rmfocus, scrollto), [])
  useEffect(() => mvSubscribe("StepList ready", ready, onReady), [])
  useEffect(() => mvSubscribe("StepList gready", gready, onGready), [])
  useEffect(() => mvSubscribe("StepList newstepindex", newstepindex, onNewstepindex), [])
  useEffect(() => mvSubscribe("StepList roure", route, onRoute), [])

  function handleClick(i) {
    if (i === focus.get()) {
      if (onFocusClick) return onFocusClick()
      const target = rfroot.current?.querySelector(".wvFocus")
      if (!!target) target.click()
      return
    }
    rmfocus.set(i)
  }

  return (
    <Main ref={rfmain}>
      <ul ref={rflist} {...attrs}>
        {items?.map(item => (
          <Item key={item.uid} ctx={rctx} stepindex={stepindex} {...item} handleClick={handleClick} />
        ))}
      </ul>
      <StepListArrows ctx={rctx} stepindex={stepindex} />
    </Main>
  )
}
